import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuthStore } from '@/stores/authStore';
import { useCreateAccount, useUpdateAccount, useAccounts } from '@/hooks/queries/useTreasury';
import { ALGERIAN_BANKS, MOBILE_PROVIDERS, ACCOUNT_TYPES } from '@/types/treasury';
import type { Account, AccountType } from '@/types/treasury';
import { toast } from '@/components/ui/toast/useToast';
import { Loader2 } from 'lucide-react';

const accountSchema = z.object({
  name: z.string().min(1, 'Le nom du compte est requis'),
  account_number: z.string().optional(),
  type: z.enum(['bank', 'ccp', 'cash', 'mobile_money']),
  
  // Bank fields
  bank_name: z.string().optional(),
  branch: z.string().optional(),
  iban: z.string().optional(),
  swift: z.string().optional(),
  
  // CCP fields
  ccp_number: z.string().optional(),
  ccp_key: z.string().optional(),
  
  // Mobile Money fields
  mobile_provider: z.string().optional(),
  mobile_number: z.string().optional(),
  
  // Balance
  initial_balance: z.number().default(0),
  
  // Settings
  currency: z.string().default('DZD'),
  is_default: z.boolean().default(false),
  is_active: z.boolean().default(true),
  
  notes: z.string().optional(),
});

type AccountFormData = z.infer<typeof accountSchema>;

interface AccountFormProps {
  open: boolean;
  onClose: () => void;
  account?: Account;
}

export function AccountForm({ open, onClose, account }: AccountFormProps) {
  const { company } = useAuthStore();
  const { data: accounts = [] } = useAccounts(company?.id || '');
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const [selectedType, setSelectedType] = useState<AccountType>(
    account?.type || 'bank'
  );

  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: account
      ? {
          name: account.name,
          account_number: account.account_number || '',
          type: account.type,
          bank_name: account.bank_name || '',
          branch: account.branch || '',
          iban: account.iban || '',
          swift: account.swift || '',
          ccp_number: account.ccp_number || '',
          ccp_key: account.ccp_key || '',
          mobile_provider: account.mobile_provider || '',
          mobile_number: account.mobile_number || '',
          initial_balance: account.initial_balance || 0,
          currency: account.currency || 'DZD',
          is_default: account.is_default || false,
          is_active: account.is_active !== false,
          notes: account.notes || '',
        }
      : {
          name: '',
          type: 'bank',
          initial_balance: 0,
          currency: 'DZD',
          is_default: false,
          is_active: true,
        },
  });

  const onSubmit = async (data: AccountFormData) => {
    try {
      // Check for duplicates (same type + same account number)
      if (!account && data.account_number) {
        const duplicate = accounts.find(
          (acc) => acc.type === data.type && acc.account_number === data.account_number
        );
        if (duplicate) {
          toast.error('Compte existe déjà', `Un compte ${data.type} avec ce numéro existe déjà.`);
          return;
        }
      }

      // Check for duplicate CCP
      if (!account && data.type === 'ccp' && data.ccp_number) {
        const duplicate = accounts.find(
          (acc) => acc.type === 'ccp' && acc.ccp_number === data.ccp_number
        );
        if (duplicate) {
          toast.error('CCP existe déjà', 'Ce numéro CCP est déjà enregistré.');
          return;
        }
      }

      if (account) {
        // Update existing account
        await updateAccount.mutateAsync({
          id: account.id,
          ...data,
        });
        toast.success('Compte mis à jour', 'Le compte a été mis à jour avec succès.');
      } else {
        // Create new account
        await createAccount.mutateAsync({
          ...data,
          company_id: company?.id,
          current_balance: data.initial_balance, // Set current balance to initial balance
        });
        toast.success('Compte créé', 'Le compte a été créé avec succès.');
      }
      onClose();
      form.reset();
    } catch (error) {
      toast.error('Erreur', 'Une erreur est survenue lors de la sauvegarde du compte.');
      console.error('Error saving account:', error);
    }
  };

  const accountType = form.watch('type');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {account ? 'تعديل الحساب / Modifier le compte' : 'حساب جديد / Nouveau compte'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Account Type */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>نوع الحساب / Type de compte *</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      setSelectedType(value as AccountType);
                    }}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez le type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(ACCOUNT_TYPES).map(([key, value]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <span>{value.icon}</span>
                            <span>{value.labelAr} / {value.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Account Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>اسم الحساب / Nom du compte *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Compte Principal BNA" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Account Number */}
            <FormField
              control={form.control}
              name="account_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>رقم الحساب / Numéro de compte</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: 123456789" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Bank Specific Fields */}
            {accountType === 'bank' && (
              <>
                <FormField
                  control={form.control}
                  name="bank_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>البنك / Banque</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez une banque" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ALGERIAN_BANKS.map((bank) => (
                            <SelectItem key={bank} value={bank}>
                              {bank}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="branch"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>الفرع / Agence</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Agence Tlemcen Centre" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="iban"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>IBAN</FormLabel>
                        <FormControl>
                          <Input placeholder="DZ..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="swift"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SWIFT/BIC</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: BNADDZAL" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            )}

            {/* CCP Specific Fields */}
            {accountType === 'ccp' && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="ccp_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>رقم CCP / Numéro CCP (12 chiffres)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ex: 001122334455"
                          maxLength={12}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ccp_key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>مفتاح CCP / Clé CCP (2 chiffres)</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: 67" maxLength={2} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Mobile Money Specific Fields */}
            {accountType === 'mobile_money' && (
              <>
                <FormField
                  control={form.control}
                  name="mobile_provider"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>المزود / Fournisseur</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez le fournisseur" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {MOBILE_PROVIDERS.map((provider) => (
                            <SelectItem key={provider.value} value={provider.value}>
                              <div className="flex items-center gap-2">
                                <span>{provider.icon}</span>
                                <span>{provider.label}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mobile_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>رقم الهاتف / Numéro de téléphone</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: 0555123456" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {/* Initial Balance */}
            <FormField
              control={form.control}
              name="initial_balance"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>الرصيد الافتتاحي / Solde initial (DA)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ملاحظات / Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Notes supplémentaires..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Settings */}
            <div className="flex items-center gap-4">
              <FormField
                control={form.control}
                name="is_default"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="w-4 h-4"
                      />
                    </FormControl>
                    <FormLabel className="!mt-0">
                      حساب افتراضي / Compte par défaut
                    </FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="w-4 h-4"
                      />
                    </FormControl>
                    <FormLabel className="!mt-0">
                      نشط / Actif
                    </FormLabel>
                  </FormItem>
                )}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={createAccount.isPending || updateAccount.isPending}
              >
                إلغاء / Annuler
              </Button>
              <Button
                type="submit"
                disabled={createAccount.isPending || updateAccount.isPending}
                className="bg-gradient-to-r from-emerald-500 to-teal-600"
              >
                {(createAccount.isPending || updateAccount.isPending) && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {account ? 'تحديث / Mettre à jour' : 'إنشاء / Créer'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
